/**
 * Análisis avanzados del repositorio de Redes Sociales: anatomía del post ganador, dependencia
 * de virales (Pareto), frecuencia contra rendimiento, constancia de publicación, momentum con
 * regresión lineal y pronóstico, comparación contra la mediana de la red y hallazgos
 * automáticos en lenguaje natural. Lógica pura para poder probarla.
 */
import { mean, parsePostDate, pearson, quantile } from './analysisUtils';
import { engagementRate, monthsBetween, num, profileKey, sumRows } from './socialRepoUtils';

const median = (values) => quantile([...values].filter(Number.isFinite).sort((a, b) => a - b), 0.5);

/* ---------- Anatomía del post ---------- */

const EMOJI = /\p{Extended_Pictographic}/u;
const CTA = /\b(link|enlace|reg[ií]strate|reg[ií]strese|desc[aá]rga|inscr[ií]bete|compra|visita|conoce m[aá]s|escucha|lee m[aá]s|haz clic|da clic)\b/i;

/** Rasgos del texto de un post que se comparan entre sí. */
export const ANATOMY_FEATURES = [
    {
        id: 'length',
        label: 'Largo del texto',
        valueOf: (text) => {
            const n = text.trim().length;
            if (!n) return 'Sin texto';
            if (n < 100) return 'Corto (< 100)';
            if (n <= 300) return 'Medio (100–300)';
            return 'Largo (> 300)';
        },
    },
    {
        id: 'hashtags',
        label: 'Hashtags',
        valueOf: (text) => {
            const n = (text.replace(/hashtag#/gi, '#').match(/#[\p{L}\p{N}_]+/gu) || []).length;
            if (!n) return 'Sin hashtags';
            return n <= 3 ? '1 a 3' : '4 o más';
        },
    },
    { id: 'emoji', label: 'Emojis', valueOf: text => (EMOJI.test(text) ? 'Con emojis' : 'Sin emojis') },
    { id: 'question', label: 'Pregunta al público', valueOf: text => (text.includes('?') ? 'Con pregunta' : 'Sin pregunta') },
    { id: 'link', label: 'Enlace en el texto', valueOf: text => (/https?:\/\/|www\./i.test(text) ? 'Con enlace' : 'Sin enlace') },
    { id: 'cta', label: 'Llamado a la acción', valueOf: text => (CTA.test(text) ? 'Con llamado' : 'Sin llamado') },
    { id: 'mention', label: 'Menciones (@)', valueOf: text => (/(^|\s)@[\p{L}\p{N}_.]+/u.test(text) ? 'Con mención' : 'Sin mención') },
];

/**
 * Para cada rasgo y valor: número de posts, mediana de la métrica y «lift» contra la mediana de
 * todos los posts (1.5 = rinde 50% más que el post típico).
 */
export function postAnatomy(posts, scoreOf) {
    const overall = median(posts.map(scoreOf));
    return ANATOMY_FEATURES.map((feature) => {
        const groups = new Map();
        posts.forEach((row) => {
            const text = String(row.Texto || '').replace('(Sin dato)', '');
            const value = feature.valueOf(text);
            if (!groups.has(value)) groups.set(value, []);
            groups.get(value).push(scoreOf(row));
        });
        const values = Array.from(groups.entries()).map(([value, scores]) => {
            const m = median(scores);
            return { value, n: scores.length, median: m, lift: overall ? m / overall : null };
        }).sort((a, b) => (b.median ?? 0) - (a.median ?? 0));
        return { ...feature, values };
    });
}

/* ---------- Pareto ---------- */

/**
 * Concentración del rendimiento: qué parte del total aportan los mejores posts.
 * @returns {{top20: number|null, curve: Array<{posts: number, share: number}>}}
 */
export function paretoCurve(values) {
    const sorted = values.filter(v => Number.isFinite(v) && v > 0).sort((a, b) => b - a);
    const total = sorted.reduce((a, b) => a + b, 0);
    if (!sorted.length || !total) return { top20: null, curve: [] };
    const curve = [{ posts: 0, share: 0 }];
    let acc = 0;
    sorted.forEach((v, i) => {
        acc += v;
        const step = Math.max(1, Math.round(sorted.length / 50));
        if ((i + 1) % step === 0 || i === sorted.length - 1) curve.push({ posts: (i + 1) / sorted.length, share: acc / total });
    });
    const topN = Math.max(1, Math.round(sorted.length * 0.2));
    const top20 = sorted.slice(0, topN).reduce((a, b) => a + b, 0) / total;
    return { top20, curve };
}

/* ---------- Frecuencia y constancia ---------- */

const weekKey = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

/** Semanas (lunes a domingo) que tocan el periodo de meses. */
export function weeksInPeriod({ from, to }) {
    const [fy, fm] = from.split('-').map(Number);
    const [ty, tm] = to.split('-').map(Number);
    const start = new Date(fy, fm - 1, 1);
    const end = new Date(ty, tm, 0);
    const today = new Date();
    const last = end > today ? today : end;
    const keys = new Set();
    for (let d = new Date(start); d <= last; d.setDate(d.getDate() + 1)) keys.add(weekKey(d));
    return Math.max(1, keys.size);
}

/**
 * Por perfil: posts por semana, mediana de la métrica por post, % de semanas con al menos un
 * post y el mayor hueco sin publicar (días). Más la correlación frecuencia ↔ rendimiento.
 */
export function frequencyAnalysis(posts, period, scoreOf) {
    const weeks = weeksInPeriod(period);
    const byProfile = new Map();
    posts.forEach((row) => {
        const key = profileKey(row);
        if (!byProfile.has(key)) byProfile.set(key, { key, Red: row.Red, Perfil: row.Perfil, scores: [], dates: [] });
        const item = byProfile.get(key);
        item.scores.push(scoreOf(row));
        const date = parsePostDate(row['Fecha Publicación']);
        if (date) item.dates.push(date);
    });
    const profiles = Array.from(byProfile.values()).map((item) => {
        const dates = item.dates.sort((a, b) => a - b);
        const activeWeeks = new Set(dates.map(weekKey)).size;
        let maxGap = 0;
        for (let i = 1; i < dates.length; i += 1) maxGap = Math.max(maxGap, (dates[i] - dates[i - 1]) / 86400000);
        return {
            key: item.key,
            Red: item.Red,
            Perfil: item.Perfil,
            posts: item.scores.length,
            perWeek: item.scores.length / weeks,
            median: median(item.scores),
            consistency: Math.min(1, activeWeeks / weeks),
            maxGap: Math.round(maxGap),
        };
    });
    const eligible = profiles.filter(p => p.posts >= 3);
    return {
        weeks,
        profiles,
        correlation: pearson(eligible.map(p => p.perWeek), eligible.map(p => p.median)),
    };
}

/* ---------- Momentum y pronóstico ---------- */

/** Regresión lineal simple y = a + b·x; null con menos de 3 puntos. */
export function linearRegression(ys) {
    const points = ys.map((y, x) => [x, y]).filter(([, y]) => Number.isFinite(y));
    if (points.length < 3) return null;
    const mx = mean(points.map(p => p[0]));
    const my = mean(points.map(p => p[1]));
    let sxy = 0;
    let sxx = 0;
    let syy = 0;
    points.forEach(([x, y]) => {
        sxy += (x - mx) * (y - my);
        sxx += (x - mx) ** 2;
        syy += (y - my) ** 2;
    });
    if (!sxx) return null;
    const slope = sxy / sxx;
    const intercept = my - slope * mx;
    const r2 = syy ? (sxy * sxy) / (sxx * syy) : 0;
    return { slope, intercept, r2, mean: my, n: points.length };
}

/**
 * Tendencia mensual de la métrica por perfil en los últimos `window` meses hasta el cierre del
 * periodo: pendiente (cambio por mes), pendiente relativa al promedio, R² y pronóstico del mes
 * siguiente. `metric` puede ser un campo o una función de filas.
 */
export function momentum(monthlyRows, toMonth, { window = 6, metric = 'Interacciones' } = {}) {
    const [y, m] = toMonth.split('-').map(Number);
    const fromDate = new Date(y, m - window, 1);
    const from = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}`;
    const months = monthsBetween(from, toMonth);
    const byProfile = new Map();
    monthlyRows.forEach((row) => {
        if (!months.includes(row.Mes)) return;
        const key = profileKey(row);
        if (!byProfile.has(key)) byProfile.set(key, { key, Red: row.Red, Perfil: row.Perfil, byMonth: new Map() });
        const bucket = byProfile.get(key).byMonth;
        if (!bucket.has(row.Mes)) bucket.set(row.Mes, []);
        bucket.get(row.Mes).push(row);
    });
    const valueOf = typeof metric === 'function'
        ? rows => metric(rows)
        : rows => sumRows(rows, [metric])[metric];
    return Array.from(byProfile.values()).map((item) => {
        const series = months.map(month => (item.byMonth.has(month) ? valueOf(item.byMonth.get(month)) : 0));
        const fit = linearRegression(series);
        const forecast = fit ? Math.max(0, fit.intercept + fit.slope * months.length) : null;
        return {
            key: item.key,
            Red: item.Red,
            Perfil: item.Perfil,
            series,
            months,
            slope: fit?.slope ?? null,
            slopePct: fit && fit.mean ? fit.slope / fit.mean : null,
            r2: fit?.r2 ?? null,
            forecast,
            last: series[series.length - 1],
        };
    }).filter(item => item.series.some(v => v > 0));
}

/* ---------- Contra la mediana de su red ---------- */

/**
 * Tasa de interacción e interacciones por post de cada perfil divididas entre la mediana de los
 * perfiles de su misma red (1.0 = igual que el perfil típico de la red).
 */
export function networkBenchmark(posts) {
    const byProfile = new Map();
    posts.forEach((row) => {
        const key = profileKey(row);
        if (!byProfile.has(key)) byProfile.set(key, { key, Red: row.Red, Perfil: row.Perfil, rows: [] });
        byProfile.get(key).rows.push(row);
    });
    const profiles = Array.from(byProfile.values()).map(item => ({
        ...item,
        rate: engagementRate(item.rows),
        perPost: item.rows.reduce((acc, r) => acc + num(r.Interacciones), 0) / item.rows.length,
    }));
    const byRed = new Map();
    profiles.forEach((p) => {
        if (!byRed.has(p.Red)) byRed.set(p.Red, []);
        byRed.get(p.Red).push(p);
    });
    return profiles.map((p) => {
        const peers = byRed.get(p.Red);
        const rateMedian = median(peers.map(x => x.rate).filter(v => v != null));
        const perPostMedian = median(peers.map(x => x.perPost));
        return {
            key: p.key,
            Red: p.Red,
            Perfil: p.Perfil,
            posts: p.rows.length,
            rate: p.rate,
            perPost: p.perPost,
            peers: peers.length,
            rateVsNetwork: p.rate != null && rateMedian ? p.rate / rateMedian : null,
            perPostVsNetwork: perPostMedian ? p.perPost / perPostMedian : null,
        };
    });
}

/* ---------- Hallazgos automáticos ---------- */

const pct = v => `${Math.round(v * 100)}%`;
const times = v => `${v.toFixed(1)}×`;

/**
 * Frases con lo más relevante del periodo, ordenadas por relevancia. Cada una con su tono
 * (good, bad, info) y la pestaña o sección donde se puede revisar.
 */
export function buildInsights({ current, previous, anatomy, pareto, heatBest, frequency, momentumRows, benchmark, metricLabel, formatValue }) {
    const out = [];
    const add = (score, tone, text, section) => out.push({ score, tone, text, section });

    /* Cambios de tasa por perfil contra el periodo anterior (mínimo 3 posts en ambos). */
    if (current && previous) {
        current.forEach((cur) => {
            const prev = previous.get(cur.key);
            if (!prev || cur.posts < 3 || prev.posts < 3 || !cur.rate || !prev.rate) return;
            const delta = (cur.rate - prev.rate) / prev.rate;
            if (Math.abs(delta) >= 0.3) {
                add(Math.abs(delta), delta > 0 ? 'good' : 'bad',
                    `${cur.Perfil} (${cur.Red}) ${delta > 0 ? 'subió' : 'bajó'} su tasa de interacción ${pct(Math.abs(delta))} contra el periodo anterior (${(prev.rate * 100).toFixed(2)}% → ${(cur.rate * 100).toFixed(2)}%).`,
                    'comparativa');
            }
        });
    }
    if (anatomy) {
        anatomy.forEach((feature) => {
            /* «Sin texto» no es un rasgo que se pueda copiar; no se usa para comparar. */
            const usable = feature.values.filter(v => v.n >= 5 && v.value !== 'Sin texto');
            const best = usable[0];
            const worst = usable[usable.length - 1];
            if (best && worst && best !== worst && best.median && worst.median && best.median / worst.median >= 1.4) {
                add(Math.min(2, best.median / worst.median - 1), 'info',
                    `${feature.label}: los posts «${best.value.toLowerCase()}» tienen una mediana de ${metricLabel} ${times(best.median / worst.median)} mayor que los «${worst.value.toLowerCase()}».`,
                    'contenido');
            }
        });
    }
    if (pareto && pareto.top20 != null) {
        if (pareto.top20 >= 0.7) {
            add(pareto.top20, 'bad', `Alta dependencia de virales: el 20% de los mejores posts genera el ${pct(pareto.top20)} de ${metricLabel}. El resultado del periodo es frágil.`, 'contenido');
        } else if (pareto.top20 <= 0.45) {
            add(0.5, 'good', `Rendimiento parejo: el 20% de los mejores posts genera solo el ${pct(pareto.top20)} de ${metricLabel}; el resultado no depende de unos cuantos posts.`, 'contenido');
        }
    }
    if (heatBest) {
        add(0.6, 'info', `Mejor franja para publicar: ${heatBest.label}, con mediana de ${formatValue(heatBest.median)} (${heatBest.count} posts).`, 'tiempos');
    }
    if (frequency && frequency.correlation != null && Math.abs(frequency.correlation) >= 0.4) {
        add(Math.abs(frequency.correlation), frequency.correlation > 0 ? 'good' : 'bad',
            frequency.correlation > 0
                ? `Los perfiles que publican más seguido también rinden más por post (correlación ${frequency.correlation.toFixed(2)}).`
                : `Publicar más no está ayudando: los perfiles con más posts por semana rinden menos por post (correlación ${frequency.correlation.toFixed(2)}). Puede haber saturación.`,
            'tiempos');
    }
    if (frequency) {
        frequency.profiles
            .filter(p => p.posts >= 2 && p.maxGap >= 21)
            .sort((a, b) => b.maxGap - a.maxGap)
            .slice(0, 2)
            .forEach(p => add(0.5, 'bad', `${p.Perfil} (${p.Red}) pasó ${p.maxGap} días sin publicar dentro del periodo.`, 'tiempos'));
    }
    if (momentumRows) {
        const solid = momentumRows.filter(r => r.r2 != null && r.r2 >= 0.5 && r.slopePct != null && Math.abs(r.slopePct) >= 0.15);
        solid.sort((a, b) => Math.abs(b.slopePct) - Math.abs(a.slopePct)).slice(0, 3).forEach((r) => {
            add(Math.min(1.5, Math.abs(r.slopePct)), r.slopePct > 0 ? 'good' : 'bad',
                `${r.Perfil} (${r.Red}) lleva una tendencia ${r.slopePct > 0 ? 'al alza' : 'a la baja'} de ${pct(Math.abs(r.slopePct))} por mes en ${metricLabel}; el pronóstico para el siguiente mes es ${formatValue(r.forecast)}.`,
                'tendencias');
        });
    }
    if (benchmark) {
        benchmark
            .filter(b => b.posts >= 3 && b.peers >= 3 && b.rateVsNetwork != null && b.rateVsNetwork >= 2)
            .sort((a, b) => b.rateVsNetwork - a.rateVsNetwork)
            .slice(0, 2)
            .forEach(b => add(Math.min(1.5, b.rateVsNetwork / 3), 'good', `${b.Perfil} tiene una tasa de interacción ${times(b.rateVsNetwork)} la del perfil típico de ${b.Red}.`, 'comparativa'));
    }
    return out.sort((a, b) => b.score - a.score).slice(0, 8);
}
